import { useEffect, useRef, useState } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import {
  DateTimePicker,
  DateTimePickerFieldProps
} from '@mui/x-date-pickers/DateTimePicker'
import { usePickerContext, useSplitFieldProps } from '@mui/x-date-pickers/hooks'
import MikeButton from '@mike/mike-shared-frontend/mike-button'
import { Severity } from '../../model/ISystemStatusMessage'
import {
  severityIcon,
  severityToken,
  SEVERITY_LABEL_ID
} from '../../utils/severityTokens'
import {
  DATE_TIME_FORMAT,
  isoToPickerMoment,
  nowIsoToTheMinute,
  parseUserDateTime,
  pickerMomentToIso
} from '../../utils/systemStatusFormat'
import { plainTextLength } from '../../utils/sanitizeHtml'
import BannerPreview from './BannerPreview'

export interface StatusMessageFormValues {
  type: Severity
  title: string
  body: string
  startDate: string
  expiringDate: string
}

const TITLE_MAX = 255
const BODY_MAX = 4000

// Display order of the segmented severity picker (most → least severe).
const SEVERITY_ORDER: Severity[] = [
  Severity.Informational,
  Severity.Warning,
  Severity.Critical
]

interface IProps {
  initialValues: StatusMessageFormValues
  editing: boolean
  saving: boolean
  onSubmit: (values: StatusMessageFormValues) => void
  onCancel: () => void
}

// The built-in picker field splits DATE_TIME_FORMAT into sections, and the
// `MMM` one only takes letters -- typing or pasting "13/08/2026 9:42 AM" got
// dropped on the floor. A plain text field keeps the format on display and
// lets `parseUserDateTime` accept whatever shape was typed.
interface ParsingDateFieldExtraProps {
  onResetToNow?: () => void
  resetLabel?: string
  openLabel?: string
  invalidDateText?: string
  size?: 'small' | 'medium'
  fullWidth?: boolean
  error?: boolean
  helperText?: string
}

type ParsingDateFieldProps = DateTimePickerFieldProps &
  Omit<TextFieldProps, 'value' | 'onChange' | 'defaultValue'> &
  ParsingDateFieldExtraProps

// `slotProps.field` is typed against the built-in field, which takes none of
// the TextField props our replacement does. Check the literal against our own
// props, then hand it over as the opaque bag the slot type expects.
const fieldSlotProps = (props: ParsingDateFieldExtraProps) =>
  (props as unknown) as Record<string, unknown>

const ParsingDateField = (props: ParsingDateFieldProps) => {
  const { forwardedProps } = useSplitFieldProps(props, 'date-time')
  // Picked rather than spread: the Picker also forwards its own `slots` and
  // `slotProps`, which a TextField would pass on to the DOM.
  const {
    id,
    size,
    fullWidth,
    inputRef,
    onResetToNow,
    resetLabel,
    openLabel,
    error,
    helperText,
    invalidDateText,
    InputProps
  } = forwardedProps as ParsingDateFieldProps

  const { value, setValue, setOpen, triggerRef, label } = usePickerContext()

  // Holds the raw text only while it is being edited; `null` means "show the
  // picker's value". Typing is committed on blur, so a half-typed date never
  // reaches Formik.
  const [draft, setDraft] = useState<string | null>(null)
  const displayed = value?.isValid() ? value.format(DATE_TIME_FORMAT) : ''
  const lastDisplayed = useRef(displayed)
  if (lastDisplayed.current !== displayed) {
    lastDisplayed.current = displayed
    setDraft(null)
  }

  const invalid =
    draft !== null && draft.trim() !== '' && parseUserDateTime(draft) === null

  const commit = () => {
    if (draft === null) {
      return
    }
    if (draft.trim() === '') {
      setValue(null)
      setDraft(null)
      return
    }
    const parsed = parseUserDateTime(draft)
    if (parsed) {
      setValue(parsed)
      setDraft(null)
    }
  }

  return (
    <TextField
      id={id}
      size={size}
      fullWidth={fullWidth}
      inputRef={inputRef}
      label={label}
      value={draft ?? displayed}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        }
      }}
      error={Boolean(error) || invalid}
      helperText={invalid ? invalidDateText : helperText}
      InputProps={{
        ...InputProps,
        startAdornment: onResetToNow ? (
          <InputAdornment position="start">
            <Tooltip title={resetLabel ?? ''}>
              <IconButton
                size="small"
                aria-label={resetLabel}
                onClick={onResetToNow}
                sx={{ ml: '-4px' }}
              >
                <RestartAltIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : (
          InputProps?.startAdornment
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label={openLabel}
              ref={triggerRef}
              onClick={() => setOpen(open => !open)}
              sx={{ mr: '-4px' }}
            >
              <CalendarTodayIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  )
}

const CounterRow = ({
  left,
  count,
  max
}: {
  left?: string
  count: number
  max: number
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: left ? 'space-between' : 'flex-end',
      mt: '4px'
    }}
  >
    {left && (
      <Typography sx={{ fontSize: 11, color: '#5A7383' }}>{left}</Typography>
    )}
    <Typography
      sx={{ fontSize: 11, color: count >= max ? '#C62828' : '#5A7383' }}
    >
      {count} / {max}
    </Typography>
  </Box>
)

const SystemStatusMessageForm = ({
  initialValues,
  editing,
  saving,
  onSubmit,
  onCancel
}: IProps) => {
  const intl = useIntl()
  const titleRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const t = window.setTimeout(() => titleRef.current?.focus(), 250)
    return () => window.clearTimeout(t)
  }, [])

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .required(intl.formatMessage({ id: 'systemStatus.form.titleRequired' }))
      .max(TITLE_MAX),
    // Limit the VISIBLE (sanitised, plain-text) length, not the raw markup —
    // allowed tags (links/lists) would otherwise eat into the budget and a raw
    // slice could truncate valid markup mid-tag.
    body: Yup.string().test(
      'plain-max',
      intl.formatMessage(
        { id: 'systemStatus.form.bodyTooLong' },
        { max: BODY_MAX }
      ),
      value => plainTextLength(value ?? '') <= BODY_MAX
    ),
    startDate: Yup.string().required(),
    expiringDate: Yup.string()
      .required()
      .test(
        'after-start',
        intl.formatMessage({ id: 'systemStatus.form.expiryAfterStart' }),
        function(value) {
          const { startDate } = this.parent
          if (!startDate || !value) {
            return true
          }
          return new Date(value).getTime() > new Date(startDate).getTime()
        }
      )
      .test(
        'not-in-past',
        intl.formatMessage({ id: 'systemStatus.form.expiryInPast' }),
        value => {
          if (!value) {
            return true
          }
          return new Date(value).getTime() > Date.now()
        }
      )
  })

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik: FormikProps<StatusMessageFormValues>) => {
        const {
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched
        } = formik
        const startChanged = !moment(values.startDate).isSame(
          initialValues.startDate,
          'minute'
        )
        // When editing, only flag a past start the user actually moved: an
        // already-started message keeps its legitimately-past start silently.
        // When creating, always warn — the default "now" can drift into the past
        // while the form is being filled in.
        const startInPast =
          Boolean(values.startDate) &&
          (!editing || startChanged) &&
          new Date(values.startDate).getTime() < Date.now() - 60_000
        // Not gated on `touched`: editing the start date can invalidate the
        // expiry without the expiry field itself ever being focused, so surface
        // the error whenever validation reports one.
        const expiryError = errors.expiringDate
        const invalidDateText = intl.formatMessage(
          { id: 'systemStatus.form.invalidDate' },
          { example: moment.utc().format(DATE_TIME_FORMAT) }
        )
        const openCalendarLabel = intl.formatMessage({
          id: 'systemStatus.form.openCalendar'
        })

        return (
          // Full-height flex column: the fields scroll, the footer does not.
          // `height: 100%` resolves against DialogContent, which the panel sizes
          // to the space left between its title bar and the bottom of the drawer.
          <Box
            component={Form}
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Box sx={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
              {/* Live preview */}
              <Box sx={{ mb: '16px' }}>
                <Typography
                  sx={{ fontSize: 11.5, color: '#5A7383', mb: '6px' }}
                >
                  {intl.formatMessage({ id: 'systemStatus.form.preview' })}
                </Typography>
                <BannerPreview
                  severity={values.type}
                  title={values.title}
                  body={values.body}
                />
              </Box>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
              >
                {/* Severity — segmented picker */}
                <Box>
                  <Typography
                    sx={{ fontSize: 11.5, color: '#5A7383', mb: '6px' }}
                  >
                    {intl.formatMessage({ id: 'systemStatus.form.severity' })}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={values.type}
                    onChange={(_e, val: Severity | null) => {
                      if (val !== null) {
                        setFieldValue('type', val)
                      }
                    }}
                    sx={{ display: 'flex', gap: '8px', width: '100%' }}
                  >
                    {SEVERITY_ORDER.map(value => {
                      const token = severityToken(value)
                      const Icon = severityIcon(value)
                      const selected = values.type === value
                      return (
                        <ToggleButton
                          key={value}
                          value={value}
                          sx={{
                            flex: 1,
                            flexDirection: 'column',
                            gap: '4px',
                            textTransform: 'none',
                            // Fully self-style each tile so the group's default
                            // grouped borders/margins can't clip the left edge.
                            margin: '0 !important',
                            borderRadius: '4px !important',
                            border: selected
                              ? `2px solid ${token.rail} !important`
                              : '1px solid #C6D5DF !important',
                            backgroundColor: selected ? token.bg : '#FFFFFF',
                            color: selected ? token.chipText : '#5A7383',
                            '&.Mui-selected': {
                              backgroundColor: token.bg,
                              color: token.chipText,
                              '&:hover': { backgroundColor: token.bg }
                            }
                          }}
                        >
                          <Icon sx={{ fontSize: 19, color: token.icon }} />
                          <Box sx={{ fontSize: 11.5, fontWeight: 500 }}>
                            {intl.formatMessage({
                              id: SEVERITY_LABEL_ID[value]
                            })}
                          </Box>
                        </ToggleButton>
                      )
                    })}
                  </ToggleButtonGroup>
                </Box>

                {/* Title */}
                <Box>
                  <TextField
                    fullWidth
                    inputRef={titleRef}
                    size="small"
                    label={`${intl.formatMessage({
                      id: 'systemStatus.form.title'
                    })} *`}
                    value={values.title}
                    onChange={e => {
                      // Mark touched only once the user actually types, so the
                      // "required" error can't appear from focus alone (the field
                      // is auto-focused on open).
                      setFieldTouched('title', true, false)
                      setFieldValue('title', e.target.value.slice(0, TITLE_MAX))
                    }}
                    error={Boolean(touched.title && errors.title)}
                    helperText={
                      touched.title && errors.title ? errors.title : ''
                    }
                    inputProps={{ maxLength: TITLE_MAX }}
                  />
                  <CounterRow
                    left={intl.formatMessage({
                      id: 'systemStatus.form.required'
                    })}
                    count={values.title.length}
                    max={TITLE_MAX}
                  />
                </Box>

                {/* Body — plain multiline (sanitised HTML rendered in the banner) */}
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    size="small"
                    label={intl.formatMessage({ id: 'systemStatus.form.body' })}
                    value={values.body}
                    onChange={e => setFieldValue('body', e.target.value)}
                    error={Boolean(touched.body && errors.body)}
                  />
                  <CounterRow
                    count={plainTextLength(values.body)}
                    max={BODY_MAX}
                  />
                </Box>

                {/* Start / Expiry */}
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    <DateTimePicker
                      label={intl.formatMessage({
                        id: 'systemStatus.form.start'
                      })}
                      timezone="UTC"
                      value={isoToPickerMoment(values.startDate)}
                      onChange={m =>
                        setFieldValue('startDate', pickerMomentToIso(m))
                      }
                      format={DATE_TIME_FORMAT}
                      ampm
                      localeText={{
                        todayButtonLabel: intl.formatMessage({
                          id: 'systemStatus.form.now'
                        })
                      }}
                      slots={{ field: ParsingDateField }}
                      slotProps={{
                        actionBar: { actions: ['today', 'accept', 'cancel'] },
                        field: fieldSlotProps({
                          size: 'small',
                          fullWidth: true,
                          error: startInPast,
                          helperText: startInPast
                            ? intl.formatMessage({
                                id: 'systemStatus.form.startInPast'
                              })
                            : '',
                          invalidDateText: invalidDateText,
                          openLabel: openCalendarLabel,
                          resetLabel: intl.formatMessage({
                            id: 'systemStatus.form.resetStart'
                          }),
                          onResetToNow: () =>
                            setFieldValue('startDate', nowIsoToTheMinute())
                        })
                      }}
                    />
                    <DateTimePicker
                      label={intl.formatMessage({
                        id: 'systemStatus.form.expiry'
                      })}
                      timezone="UTC"
                      value={isoToPickerMoment(values.expiringDate)}
                      onChange={m =>
                        setFieldValue('expiringDate', pickerMomentToIso(m))
                      }
                      minDateTime={
                        isoToPickerMoment(values.startDate) ?? undefined
                      }
                      format={DATE_TIME_FORMAT}
                      ampm
                      slots={{ field: ParsingDateField }}
                      slotProps={{
                        field: fieldSlotProps({
                          size: 'small',
                          fullWidth: true,
                          error: Boolean(expiryError),
                          helperText: expiryError || '',
                          invalidDateText: invalidDateText,
                          openLabel: openCalendarLabel
                        })
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
            </Box>

            {/* Footer — the column's last row, so it sits at the bottom of the
                panel without leaving the flow and hiding content behind itself.
                The negative margins bleed it over DialogContent's 24px side and
                20px bottom padding so it spans the full panel width. */}
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'start',
                alignItems: 'center',
                gap: '1rem',
                mx: '-24px',
                mb: '-20px',
                px: '24px',
                py: '14px',
                backgroundColor: '#FFFFFF',
                borderTop: '1px solid #DCE6ED'
              }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={!formik.dirty || !formik.isValid || saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    undefined
                  )
                }
              >
                {intl.formatMessage({
                  id: editing
                    ? 'systemStatus.form.save'
                    : 'systemStatus.form.create'
                })}
              </Button>
              <MikeButton onClick={onCancel} buttontype="text">
                {intl.formatMessage({ id: 'systemStatus.common.cancel' })}
              </MikeButton>
            </Box>
          </Box>
        )
      }}
    </Formik>
  )
}

export default SystemStatusMessageForm
